import {
  createClient,
  mergeHeaders,
  RequestOptions,
} from "@smp/api/build/openapi/client";
import {
  Failure,
  popupWindowOpen,
  REACT_APP__CANVA_CONNECT_API_URL,
} from "@smp/utils";

type AuthResponse =
  | {
      type: "success";
      token: string;
    }
  | {
      type: "error";
      message: string;
    };

const SESSION_STORAGE_KEY = "ep.canva_token";

let token: string =
  typeof window !== "undefined"
    ? sessionStorage.getItem(SESSION_STORAGE_KEY) || ""
    : "";
let tokenUpdatePromise: Promise<string> | undefined;

const isRequestOptionsWithFetch = (
  options: unknown,
): options is RequestOptions & {
  fetch: NonNullable<RequestOptions["fetch"]>;
} => {
  if (typeof options === "object" && options !== null && "fetch" in options) {
    return true;
  }

  return false;
};

let tokenUpdateAbortController: AbortController | undefined;

const updateToken = () => {
  if (tokenUpdateAbortController) {
    tokenUpdateAbortController.abort();
    tokenUpdatePromise = undefined;
    tokenUpdateAbortController = undefined;
  }

  tokenUpdateAbortController = new AbortController();

  tokenUpdatePromise =
    tokenUpdatePromise ||
    popupWindowOpen(
      "http://127.0.0.1:3001/get-token",
      "canva_oauth_complete",
      tokenUpdateAbortController.signal,
    )
      .then((resp: AuthResponse) => {
        if (resp.type === "success") {
          token = resp.token;
        } else {
          token = "";
        }

        if (typeof window !== "undefined") {
          sessionStorage.setItem(SESSION_STORAGE_KEY, token);
        }
        return token;
      })
      .catch((error) => {
        if (error instanceof Failure && error.code === "ROOT__UNKNOWN") {
          return "";
        }

        throw error;
      })
      .finally(() => {
        tokenUpdatePromise = undefined;
      });

  return tokenUpdatePromise;
};

export const canvaApiClient = createClient({
  headers: {
    Authorization: `Bearer ${token}`,
  },
  baseUrl: REACT_APP__CANVA_CONNECT_API_URL,
  auth: async () => token || (await updateToken()),
  requestValidator: (opts) =>
    new Promise(async (resolve) => {
      if (!!token || !isRequestOptionsWithFetch(opts)) {
        resolve(undefined);
      } else {
        opts.fetch = () =>
          Promise.resolve(
            new Response(
              JSON.stringify({
                code: "unauthorized_client",
                message: "Auth aborted!",
              }),
              {
                status: 400,
                statusText: "AUTH_ABORTED",
              },
            ),
          );
        resolve(undefined);
      }
    }),
  fetch: (input, init) =>
    fetch(input, init).then(async (resp) => {
      if (resp.status === 401) {
        const newToken = await updateToken();
        const pathcedHeaders = mergeHeaders(init?.headers || {}, {
          Authorization: `Bearer ${newToken}`,
        });

        return fetch(input, {
          ...init,
          headers: pathcedHeaders,
        });
      }

      return resp;
    }),
});
